import { createFileRoute } from "@tanstack/react-router";
import { RequireAuth } from "@/components/RequireAuth";
import { useEffect, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { UploadCloud, FileSpreadsheet, AlertCircle, X, CheckCircle2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/upload")({
  component: () => <RequireAuth><UploadPage /></RequireAuth>,
});

interface ParsedRow {
  ordem: string | null;
  oper: string | null;
  trb_real: number | null;
  cen_tr_real: string | null;
  inicio_real: string | null;
  fim_exec: string | null;
  denominacao_centro: string | null;
  num_pessoa: string | null;
  nome_empregado: string | null;
}

// Aliases for simple/text columns
const COLUMN_ALIASES = {
  ordem: ["Ordem"],
  oper: ["Oper", "Oper."],
  trb_real: ["TrbReal", "Trb.real", "Trb real"],
  cen_tr_real: ["CenTrReal", "Cen.Tr.Real", "CenTr.Real"],
  denominacao_centro: ["Denominação centro trabalho", "Denominacao centro trabalho"],
  num_pessoa: ["Nº pess.", "No pess.", "Nº pess"],
  nome_empregado: ["Nome do empregado"],
  // Date-only columns (dd.mm.aaaa)
  data_inicio_exec: ["Iníc.exec.", "Inic.exec.", "Início execução", "Inicio execução"],
  data_fim_real: ["DtaFimReal", "Dta Fim Real", "Data fim real", "Data fim"],
  // Time-only columns
  hora_inicio: ["Iníc.real", "Inic.real", "Iníc real", "Inic real", "Hora início", "Hora inicio"],
  hora_fim: ["Fim exec.", "Fim exec", "Fim execução", "Hora fim", "Hora término", "Hora termino"],
} as const;

type ColKey = keyof typeof COLUMN_ALIASES;

function normalizeKey(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, " ")
    .trim()
    .toLowerCase();
}

function pick(row: Record<string, unknown>, key: ColKey): unknown {
  const entries = Object.entries(row);
  for (const alias of COLUMN_ALIASES[key]) {
    const normalizedAlias = normalizeKey(alias);
    const match = entries.find(([rowKey, rowValue]) => normalizeKey(rowKey) === normalizedAlias && rowValue !== undefined && rowValue !== null && rowValue !== "");
    if (match) return match[1];
  }
  return null;
}

// Parse a date-only value (returns {y,m,d} in local time)
function parseDateOnly(v: unknown): { y: number; m: number; d: number } | null {
  if (v == null || v === "") return null;
  if (v instanceof Date) return { y: v.getFullYear(), m: v.getMonth(), d: v.getDate() };
  if (typeof v === "number") {
    const ms = Math.round((v - 25569) * 86400 * 1000);
    const dt = new Date(ms);
    return { y: dt.getUTCFullYear(), m: dt.getUTCMonth(), d: dt.getUTCDate() };
  }
  const s = String(v).trim();
  // dd.mm.yyyy  OR  dd/mm/yyyy  OR  dd-mm-yyyy
  const m = s.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})/);
  if (m) return { y: +m[3], m: +m[2] - 1, d: +m[1] };
  // yyyy-mm-dd
  const m2 = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m2) return { y: +m2[1], m: +m2[2] - 1, d: +m2[3] };
  const dt = new Date(s);
  if (!isNaN(dt.getTime())) return { y: dt.getFullYear(), m: dt.getMonth(), d: dt.getDate() };
  return null;
}

// Parse a time-only value into {h,m,s}
function parseTimeOnly(v: unknown): { h: number; m: number; s: number } | null {
  if (v == null || v === "") return null;
  if (v instanceof Date) return { h: v.getUTCHours(), m: v.getUTCMinutes(), s: v.getUTCSeconds() };
  if (typeof v === "number") {
    // Excel time as fraction of day
    const total = Math.round(v * 86400);
    return { h: Math.floor(total / 3600) % 24, m: Math.floor((total % 3600) / 60), s: total % 60 };
  }
  const s = String(v).trim();
  const m = s.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (m) return { h: +m[1], m: +m[2], s: +(m[3] ?? 0) };
  return null;
}

function combineDateTime(d: ReturnType<typeof parseDateOnly>, t: ReturnType<typeof parseTimeOnly>): string | null {
  if (!d) return null;
  const hh = t?.h ?? 0, mm = t?.m ?? 0, ss = t?.s ?? 0;
  return new Date(d.y, d.m, d.d, hh, mm, ss).toISOString();
}

function UploadPage() {
  const { user, roles } = useAuth();
  const qc = useQueryClient();
  const canUpload = roles.includes("adm") || roles.includes("atualizador");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<ParsedRow[] | null>(null);
  const [fileName, setFileName] = useState<string>("");
  const [fileSize, setFileSize] = useState<number>(0);
  const [reading, setReading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!reading) return;
    setProgress(8);
    const id = setInterval(() => {
      setProgress((p) => (p < 90 ? p + Math.max(1, (95 - p) * 0.12) : p));
    }, 120);
    return () => clearInterval(id);
  }, [reading]);

  if (!canUpload) {
    return (
      <div className="max-w-xl mx-auto mt-12 p-8 bg-card border border-border rounded-xl text-center shadow-sm">
        <AlertCircle className="h-10 w-10 mx-auto text-warning mb-3" />
        <h2 className="text-lg font-semibold">Sem permissão</h2>
        <p className="text-muted-foreground mt-1 text-sm">Apenas usuários ADM ou Atualizador podem fazer upload.</p>
      </div>
    );
  }

  const handleFile = async (file: File) => {
    setFileName(file.name);
    setFileSize(file.size);
    setReading(true);
    setProgress(5);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array", cellDates: true });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws, { defval: null });
      const parsed: ParsedRow[] = json.map((row) => {
        const dIni = parseDateOnly(pick(row, "data_inicio_exec"));
        const tIni = parseTimeOnly(pick(row, "hora_inicio"));
        const dFim = parseDateOnly(pick(row, "data_fim_real")) ?? dIni;
        const tFim = parseTimeOnly(pick(row, "hora_fim"));
        let inicioReal = combineDateTime(dIni, tIni);
        let fimExec = combineDateTime(dFim, tFim);
        // overnight: end before start → add 1 day
        if (inicioReal && fimExec && new Date(fimExec).getTime() < new Date(inicioReal).getTime()) {
          fimExec = new Date(new Date(fimExec).getTime() + 86400000).toISOString();
        }
        const ordem = pick(row, "ordem");
        const oper = pick(row, "oper");
        const centroCodigo = pick(row, "cen_tr_real");
        const centroNome = pick(row, "denominacao_centro");
        const pessoa = pick(row, "num_pessoa");
        const empregado = pick(row, "nome_empregado");
        const trbRealRaw = pick(row, "trb_real");
        // Trust TrbReal as-is (preserve zeros). Only derive if column is absent.
        let trb: number | null = null;
        if (trbRealRaw != null && trbRealRaw !== "") {
          const n = Number(trbRealRaw);
          if (!Number.isNaN(n) && Number.isFinite(n)) trb = n;
        } else if (inicioReal && fimExec) {
          const diff = (new Date(fimExec).getTime() - new Date(inicioReal).getTime()) / 60000;
          if (Number.isFinite(diff) && diff >= 0) trb = Math.round(diff);
        }

        return {
          ordem: ordem ? String(ordem).trim() : null,
          oper: oper ? String(oper).trim() : null,
          trb_real: trb,
          cen_tr_real: centroCodigo ? String(centroCodigo).trim() : null,
          inicio_real: inicioReal,
          fim_exec: fimExec,
          denominacao_centro: centroNome ? String(centroNome).trim() : null,
          num_pessoa: pessoa ? String(pessoa).trim() : null,
          nome_empregado: empregado ? String(empregado).trim() : null,
        };
      });
      setProgress(100);
      setPreview(parsed);
      toast.success(`${parsed.length.toLocaleString("pt-BR")} linhas lidas com sucesso`);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setTimeout(() => setReading(false), 350);
    }
  };

  const handleSave = async (replace: boolean) => {
    if (!preview || !user) return;
    setBusy(true);
    try {
      if (replace) {
        const { error } = await supabase.from("apontamentos").delete().not("id", "is", null);
        if (error) throw error;
      }
      const payload = preview.map((r) => ({ ...r, uploaded_by: user.id }));
      const chunkSize = 500;
      for (let i = 0; i < payload.length; i += chunkSize) {
        const { error } = await supabase.from("apontamentos").insert(payload.slice(i, i + chunkSize));
        if (error) throw error;
      }
      toast.success(`${payload.length.toLocaleString("pt-BR")} apontamentos salvos`);
      setPreview(null);
      setFileName("");
      setFileSize(0);
      qc.invalidateQueries({ queryKey: ["apontamentos"] });
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const fmtSize = (b: number) => b < 1024 * 1024 ? `${(b / 1024).toFixed(1)} KB` : `${(b / (1024 * 1024)).toFixed(2)} MB`;

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Upload de Dados</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Importe uma planilha de apontamentos para análise automática.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-[14px] font-semibold">Nova importação</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              const f = e.dataTransfer.files?.[0];
              if (f) handleFile(f);
            }}
            onClick={() => inputRef.current?.click()}
            className={cn(
              "border-2 border-dashed rounded-xl p-10 text-center transition-all cursor-pointer",
              dragging
                ? "border-primary bg-primary/5 scale-[1.005]"
                : "border-border bg-muted/30 hover:bg-muted/50 hover:border-foreground/30",
            )}
          >
            <div className="h-14 w-14 rounded-full bg-card border border-border mx-auto flex items-center justify-center mb-4 shadow-sm">
              <UploadCloud className="h-6 w-6 text-muted-foreground" strokeWidth={1.8} />
            </div>
            <p className="text-sm font-medium">
              Arraste o arquivo aqui ou <span className="text-primary underline-offset-2 underline">selecione no computador</span>
            </p>
            <p className="text-xs text-muted-foreground mt-1.5">
              Formatos suportados: XLSX, XLS, CSV · até 20 MB
            </p>
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
            />
          </div>

          {(fileName || reading) && (
            <div className="border border-border rounded-lg p-4 bg-card">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <FileSpreadsheet className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-medium truncate">{fileName}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {fileSize > 0 && fmtSize(fileSize)}
                    {preview && ` · ${preview.length.toLocaleString("pt-BR")} linhas`}
                  </div>
                </div>
                {preview && !reading && (
                  <CheckCircle2 className="h-5 w-5 text-success" />
                )}
                {!reading && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={(e) => { e.stopPropagation(); setPreview(null); setFileName(""); setFileSize(0); setProgress(0); }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              {(reading || (progress > 0 && progress < 100)) && (
                <div className="mt-3">
                  <Progress value={progress} className="h-1.5" />
                  <div className="text-[10.5px] text-muted-foreground mt-1.5">
                    Processando arquivo... {Math.round(progress)}%
                  </div>
                </div>
              )}
            </div>
          )}

          <p className="text-[12px] text-muted-foreground">
            Colunas esperadas: <strong className="text-foreground">Ordem, Oper, TrbReal, CenTrReal, Iníc.real, Fim exec., Denominação centro trabalho, Nº pess., Nome do empregado.</strong>
          </p>

          {preview && (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2 items-center justify-between border-t border-border pt-4">
                <div className="text-[13px]">
                  <strong>{preview.length.toLocaleString("pt-BR")}</strong> linhas prontas para importação.
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => handleSave(false)} disabled={busy}>Adicionar (acumular)</Button>
                  <Button onClick={() => handleSave(true)} disabled={busy}>
                    {busy ? "Salvando..." : "Substituir dados existentes"}
                  </Button>
                </div>
              </div>
              <div className="overflow-x-auto rounded-lg border border-border max-h-80">
                <table className="w-full text-[12px]">
                  <thead className="bg-muted/40 sticky top-0">
                    <tr className="text-left">
                      {["Ordem","Oper","TrbReal","Centro","Início","Fim","Nº pess.","Empregado"].map((h) => (
                        <th key={h} className="px-3 py-2 font-medium text-muted-foreground uppercase tracking-wider text-[10.5px]">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.slice(0, 20).map((r, i) => (
                      <tr key={i} className="border-t border-border">
                        <td className="px-3 py-1.5 font-mono">{r.ordem}</td>
                        <td className="px-3 py-1.5">{r.oper}</td>
                        <td className="px-3 py-1.5 tabular-nums">{r.trb_real}</td>
                        <td className="px-3 py-1.5 max-w-[160px] truncate">{r.cen_tr_real}</td>
                        <td className="px-3 py-1.5 text-muted-foreground">{r.inicio_real?.slice(0,16).replace("T"," ")}</td>
                        <td className="px-3 py-1.5 text-muted-foreground">{r.fim_exec?.slice(0,16).replace("T"," ")}</td>
                        <td className="px-3 py-1.5">{r.num_pessoa}</td>
                        <td className="px-3 py-1.5 max-w-[180px] truncate">{r.nome_empregado}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {preview.length > 20 && (
                  <div className="text-center text-[11px] text-muted-foreground py-2 border-t border-border bg-muted/20">
                    Mostrando 20 de {preview.length.toLocaleString("pt-BR")} linhas
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
